import React, { useEffect, useRef } from "react";
// ANT DESIGN COMPONENT
import { Tooltip, notification, Tag } from "antd";
// COPY TO CLIP BOARD
import { CopyToClipboard } from "react-copy-to-clipboard";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateTerminalAutoScroll } from "../../../redux/monitoring/monitoringSlice";
// INTERFACES
import { RootState } from "../../../app/store";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { uuidToString } from "../../../utils/uuid";

/** JSON replacer that serialises BigInt values as strings. */
function bigIntReplacer(_key: string, value: unknown): unknown {
  return typeof value === "bigint" ? value.toString() : value;
}

/** Strip the large compound-keyed maps from a socket message for display. */
function compactMessage(msg: SocketMessageInterface): Record<string, unknown> {
  const { bitmask, positionMap, serviceMap, bankId, liftId, ...rest } = msg;
  return {
    ...rest,
    bankId: uuidToString(bankId),
    liftId: uuidToString(liftId),
    bitmask: `{${Object.keys(bitmask).length} entries}`,
    positionMap,
    serviceMap,
  };
}

function TerminalJsonViewer({
  socketMessages,
}: {
  socketMessages: SocketMessageInterface[];
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");
  const bottomRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { terminalAutoScroll } = useSelector(
    (state: RootState) => state.monitoring
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const launchNotification = () => {
    notification.success({
      message: "Text copied to clipboard",
      placement: "top",
      className: `monitoring__notification__${theme}`,
    });
    dispatch(updateTerminalAutoScroll(false));
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (socketMessages && terminalAutoScroll) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [socketMessages]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className={`terminal__container__${theme}`}>
      <div
        className="generalStyles__width100 terminal__hoverLog"
        style={{ height: "400px", overflowY: "auto" }}
      >
        <div className="terminal-logs">
          {socketMessages.map((msg, index) => (
            <div key={index} className="log-item mt-3">
              <Tag color="gold">{index + 1}</Tag>
              <span
                className="log-prefix"
                style={{ color: "#38ac68", marginRight: "5px" }}
              >
                --&gt;&gt;
              </span>{" "}
              <CopyToClipboard
                text={JSON.stringify(compactMessage(msg), bigIntReplacer)}
                onCopy={launchNotification}
              >
                <Tooltip placement="top" title={t("general.copyToClipboard")}>
                  {JSON.stringify(compactMessage(msg), bigIntReplacer)}
                </Tooltip>
              </CopyToClipboard>
            </div>
          ))}
        </div>
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

export default TerminalJsonViewer;
